import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { Header, Footer } from "../components/Layout";
import { Clock, FileText, ShieldCheck, Bell, Send, Download, ArrowLeft, AlertTriangle, CheckCircle2, Circle } from "lucide-react";

const TYPE_META = {
  case: { icon: <ShieldCheck className="w-4 h-4" />, label: "Dossier", cls: "bg-slate-800 text-emerald-300" },
  doc: { icon: <FileText className="w-4 h-4" />, label: "Document", cls: "bg-emerald-100 text-emerald-800" },
  action: { icon: <Send className="w-4 h-4" />, label: "Action", cls: "bg-blue-100 text-blue-800" },
  alert: { icon: <Bell className="w-4 h-4" />, label: "Rappel", cls: "bg-amber-100 text-amber-800" },
};

const LEVEL_DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };

export default function Timeline() {
  const { caseId } = useParams();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(true);

  const scopeId = caseId || params.get("case_id") || "";

  useEffect(() => {
    if (loading) return;
    if (!user) { window.location.href = "/login"; return; }
    setBusy(true);
    api.get(`/timeline/me${scopeId ? `?case_id=${encodeURIComponent(scopeId)}` : ""}`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setBusy(false));
  }, [user, loading, scopeId]);

  const exportPdf = async () => {
    try {
      const r = await api.get(`/timeline/pdf${scopeId ? `?case_id=${encodeURIComponent(scopeId)}` : ""}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jurisflash-timeline-${scopeId || "me"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Export PDF impossible. Réessayez.");
    }
  };

  if (loading || busy) return <div className="min-h-screen flex items-center justify-center"><div className="pulse-dot"></div></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1E293B] mb-4"><ArrowLeft className="w-4 h-4" /> Retour</Link>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="j-label mb-2 flex items-center gap-2"><Clock className="w-3 h-3" /> Chronologie {scopeId ? "du dossier" : "globale"}</div>
            <h1 className="j-h1 font-heading" data-testid="timeline-title">Votre timeline juridique</h1>
            <p className="text-slate-600 mt-1 text-sm">{events.length} événement(s) · docs scannés, actions, rappels et Flash Droit</p>
          </div>
          <button onClick={exportPdf} className="btn-navy" data-testid="timeline-export-btn"><Download className="w-4 h-4" /> Export PDF audience</button>
        </div>

        {events.length === 0 && (
          <div className="j-card text-center text-slate-500 py-16" data-testid="timeline-empty">
            <Circle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            Aucun événement pour l'instant.
            <div className="mt-3"><Link to="/" className="text-emerald-700 font-semibold hover:underline">Lancer un Flash Droit</Link></div>
          </div>
        )}

        <div className="relative" data-testid="timeline-list">
          {events.map((ev, i) => (
            <div key={i} className="flex gap-4 pb-6 relative" data-testid={`timeline-event-${i}`}>
              {/* Rail vertical */}
              {i < events.length - 1 && <div className="absolute left-4 top-8 bottom-0 w-px bg-slate-200"></div>}
              {/* Puce niveau */}
              <div className={`w-8 h-8 rounded-full ${LEVEL_DOT[ev.level] || "bg-slate-400"} flex items-center justify-center text-white flex-shrink-0 z-10 shadow-sm`}>
                {(TYPE_META[ev.type] || TYPE_META.case).icon}
              </div>
              <div className="flex-1 min-w-0 j-card !p-4">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[0.6rem] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${(TYPE_META[ev.type] || TYPE_META.case).cls}`}>
                    {(TYPE_META[ev.type] || TYPE_META.case).label}
                  </span>
                  <span className="text-xs text-slate-500 font-mono">{ev.at ? new Date(ev.at).toLocaleString("fr-FR") : ""}</span>
                </div>
                <div className="font-heading font-bold text-[#1E293B]">{ev.title}</div>
                {ev.detail && <p className="text-sm text-slate-600 mt-1 leading-relaxed">{ev.detail}</p>}
                {ev.case_id && ev.type === "case" && (
                  <Link to={`/case/${ev.case_id}`} className="text-xs text-emerald-700 hover:underline mt-2 inline-block">Voir le dossier →</Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
